<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-user" class="q-mb-sm" /> {{ $t('user') }}
		</div>

		<div class="row gutter-xs">
			<div class="col-xs-12 col-sm-12">
				<span class="q-headline text-weight-light">{{ $t('personal_data') }}</span>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.name">
				<q-input
					v-model="full_name"
					ref="full_name"
					:float-label="`${$t('name_and_last_name')} ${hasObrigatory('full_name')}`"
					:error="$v.full_name.$error"
					@input="$v.full_name.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.nick_name">
				<q-input
					v-model="nick_name"
					ref="nick_name"
					:float-label="`${$t('nick_name')} ${hasObrigatory('nick_name')}`"
					:disable="disableDataWebservice || managerUser"
					:error="$v.nick_name.$error"
					@input="$v.nick_name.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.email_fields">
				<q-field
					:error="email_error || email_error_prefix"
					:error-label="messageErrorEmail"
				>
					<q-input
						v-model="email"
						ref="email"
						type="email"
						:float-label="`${$t('email')} ${hasObrigatory('email')}`"
						:error="$v.email.$error"
						@input="$v.email.$touch"
						@blur="validateEmail()"
						:disable="disableDataWebservice || managerUser"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="email_error && id_user">
				<q-btn
					color="secondary"
					size="small"
					:label="$t('edit_user_found')"
					icon="fal fa-edit"
					@click="openUser()"
				/>
			</div>

			<div
				class="col-xs-12 col-sm-6"
				v-if="register_store.use_document_mask && countries.length > 1"
			>
				<q-select
					v-model="user_country"
					ref="country_of_residence"
					:options="countries.map((e) => ({ ...e, label: $t(e.label) }))"
					:float-label="$t('country_of_residence')"
					@clear="document = ''"
					clearable
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div
				class="col-xs-12 col-sm-6"
				v-if="register_fields.document && register_store.use_document_mask"
			>
				<input
					v-show="false"
					type="text"
					v-mask="
						`${$mask.document({
							...userCountryObjected,
							config: register_store.use_document_mask,
						})}`
					"
					v-model="document"
				/>
				<q-field
					:error="document_error || document_invalid"
					:error-label="$t(documentError)"
				>
					<q-input
						v-model="document"
						ref="document"
						:float-label="`${$t(documentIsCPF)} ${hasObrigatory('document')}`"
						:error="$v.document.$error"
						@input="$v.document.$touch"
						@blur="documentUnique()"
						:disable="disableDataWebservice || managerUser || user_country == null"
						@keydown.space.prevent
						v-mask="
							`${$mask.document({
								...userCountryObjected,
								config: register_store.use_document_mask,
							})}`
						"
					/>
				</q-field>
				<small
					v-if="
						isChevrolet &&
							id_user_language_selected == 1 &&
							id_user_client_country_selected == 32
					"
					class="text-faded"
					>{{ $t('document_exemple') }}</small
				>
			</div>

			<div
				class="col-xs-12 col-sm-6"
				v-if="register_fields.document && !register_store.use_document_mask"
			>
				<q-field
					:error="document_error || document_invalid"
					:error-label="$t(documentError)"
				>
					<q-input
						v-model="document"
						ref="document"
						:float-label="`${$t(documentIsCPF)} ${hasObrigatory('document')}`"
						:error="$v.document.$error"
						@input="$v.document.$touch"
						@blur="documentUnique()"
						:disable="disableDataWebservice || managerUser"
						@keydown.space.prevent
					/>
				</q-field>
				<small
					v-if="
						isChevrolet &&
							id_user_language_selected == 1 &&
							id_user_client_country_selected == 32
					"
					class="text-faded"
					>{{ $t('document_exemple') }}</small
				>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.register && !isChevrolet">
				<q-field
					:error="register_error"
					:error-label="register_error ? $t('register_not_unique') : ''"
				>
					<q-input
						v-model="register"
						ref="register"
						:float-label="`${$t('register')} ${hasObrigatory('register')}`"
						:error="$v.register.$error"
						@input="$v.register.$touch"
						@blur="registerUnique()"
						:disable="disableDataWebservice || managerUser"
					/>
				</q-field>
			</div>

			<div class="col-sm-6" v-if="register_error"></div>

			<div class="col-xs-12 col-sm-6" v-if="(document_error || register_error) && id_user">
				<q-btn
					color="secondary"
					size="small"
					:label="$t('edit_user_found')"
					icon="fal fa-edit"
					@click="openUser()"
				/>
			</div>

			<div class="col-sm-6" v-if="document_error"></div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.register_2">
				<q-field
					:error="register_2_error"
					:error-label="register_2_error ? $t('gmin_number_not_unique') : ''"
				>
					<q-input
						disable
						v-model="register_2"
						ref="register_2"
						:float-label="`${$t('gmin_number')} ${hasObrigatory('gmin_number')}`"
						:error="$v.register_2.$error"
						@input="$v.register_2.$touch"
						:disable="disableDataWebservice || managerUser"
						@blur="register2Unique()"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.birthday">
				<q-datetime
					v-model="birthday"
					ref="birthday"
					type="date"
					:float-label="`${$t('birthday')} ${hasObrigatory('birthday')}`"
					format="DD/MM/YYYY"
					:error="$v.birthday.$error"
					@input="$v.birthday.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.gender">
				<q-select
					v-model="gender"
					ref="gender"
					:options="getUserGenders"
					:float-label="`${$t('gender')} ${hasObrigatory('gender')}`"
					:error="$v.gender.$error"
					@input="$v.gender.$touch"
					clearable
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.number_of_children">
				<q-input
					v-model="number_of_children"
					ref="number_of_children"
					type="number"
					:float-label="
						`${$t('number_of_children')} ${hasObrigatory('number_of_children')}`
					"
					:error="$v.number_of_children.$error"
					@input="$v.number_of_children.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.marital_status">
				<q-select
					v-model="marital_status"
					ref="marital_status"
					:options="marital_statuss"
					:error="$v.marital_status.$error"
					@input="$v.marital_status.$touch"
					:float-label="`${$t('marital')} ${hasObrigatory('marital_status')}`"
					clearable
					:disable="disableDataWebservice || managerUser"
				/>
			</div>
		</div>

		<div class="row gutter-xs">
			<div class="col-xs-12 col-sm-6" v-if="register_fields.is_handicapped">
				<q-select
					v-model="is_handicapped"
					ref="is_handicapped"
					:options="[
						{ value: true, label: $t('yes') },
						{ value: false, label: $t('no') },
					]"
					:float-label="`${$t('is_handicapped')} ${hasObrigatory('is_handicapped')}`"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.handicap_description">
				<q-input
					v-model="handicap_description"
					ref="handicap_description"
					:float-label="
						`${$t('handicap_description')} ${hasObrigatory('handicap_description')}`
					"
					:error="$v.handicap_description.$error"
					@input="$v.handicap_description.$touch"
					:disable="disableDataWebservice || managerUser || !is_handicapped"
				/>
			</div>
		</div>

		<div class="row gutter-xs">
			<div class="col-xs-12 col-sm-12 q-mt-lg" v-if="showPhones">
				<span class="q-headline text-weight-light">{{ $t('phone_number') }}</span>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.mobile_phone_fields">
				<q-input
					v-model="phone_mobile.phone"
					ref="phone_mobile"
					:float-label="`${$t('mobile')} ${hasObrigatory('phone_mobile')}`"
					:error="$v.phone_mobile.phone.$error"
					@input="$v.phone_mobile.phone.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.whatsapp_phone_fields">
				<q-input
					v-model="phone_whatsapp.phone"
					ref="phone_whatsapp"
					:float-label="`${$t('whatsapp')} ${hasObrigatory('whatsapp_phone')}`"
					:error="$v.phone_whatsapp.phone.$error"
					@input="$v.phone_whatsapp.phone.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.commercial_phone_fields">
				<q-input
					v-model="phone_commercial.phone"
					ref="phone_commercial"
					:float-label="`${$t('commercial')} ${hasObrigatory('commercial_phone')}`"
					:error="$v.phone_commercial.phone.$error"
					@input="$v.phone_commercial.phone.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.residential_phone_fields">
				<q-input
					v-model="phone_residential.phone"
					ref="phone_residential"
					:float-label="`${$t('residential')} ${hasObrigatory('residential_phone')}`"
					:error="$v.phone_residential.phone.$error"
					@input="$v.phone_residential.phone.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-12 q-mt-lg" v-if="register_fields.address_fields">
				<span class="q-headline text-weight-light">{{ $t('address') }}</span>
			</div>

			<div
				v-if="keyGooogleMaps && register_fields.address_fields"
				class="col-xs-12 col-sm-12"
			>
				<q-field
					:error="show_maps_alert"
					:error-label="
						$t(
							'not_found_check_that_the_search_is_spelled_correctly_Try_adding_a_city_state_or_zip_code'
						)
					"
				>
					<q-input
						v-model="address"
						ref="address"
						:float-label="`${$t('address')} ${hasObrigatory('address')}`"
						autocomplete="new-password"
						:error="$v.address.$error"
						@input="$v.address.$touch"
						:placeholder="$t('search_by_name_and_street_number')"
					>
						<q-autocomplete
							@search="getAddressGoogleMaps"
							:min-characters="3"
							@selected="selectedAddressGoogleMaps"
							separator
						/>
					</q-input>
				</q-field>
			</div>

			<div
				v-else-if="!keyGooogleMaps && register_fields.address_fields"
				class="col-xs-12 col-sm-12"
			>
				<q-input
					v-model="address"
					:float-label="`${$t('address')} ${hasObrigatory('address')}`"
					autocomplete="new-password"
					:error="$v.address.$error"
					@input="$v.address.$touch"
					:disable="disableDataWebservice || managerUser"
				>
					<q-autocomplete
						@search="getAddress"
						:min-characters="3"
						@selected="selectedAddress"
						separator
					/>
				</q-input>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="number"
					:float-label="$t('number')"
					ref="number"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="complement"
					:float-label="$t('complement')"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="neighborhood"
					:float-label="$t('neighborhood')"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="postal_code"
					:float-label="$t('postal_code')"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="city"
					ref="city"
					:float-label="`${$t('city')} ${hasObrigatory('city')}`"
					:error="$v.city.$error"
					@input="$v.city.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="state"
					ref="state"
					:float-label="`${$t('state')} ${hasObrigatory('state')}`"
					:error="$v.state.$error"
					@input="$v.state.$touch"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-12 q-mt-lg">
				<span
					v-if="
						register_fields.regional ||
							register_fields.company ||
							register_fields.store ||
							register_fields.managers ||
							register_fields.admission_date
					"
					class="q-headline text-weight-light"
					>{{ $t('organization_data') }}</span
				>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.regional">
				<q-select
					multiple
					chips
					v-model="branch_regionals"
					ref="branch_regionals"
					:options="branch_regional"
					:error="$v.branch_regionals.$error"
					@input="$v.branch_regionals.$touch"
					:float-label="
						`${$t('regionals')} (${branch_regionals.length}) ${hasObrigatory(
							'branch_regionals'
						)}`
					"
					:disable="disableDataWebservice || managerUser || hasCreateStore"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.company">
				<q-chips-input
					add-icon="   "
					v-model="companies_label"
					ref="companies_label"
					:float-label="
						`${$t('companies_or_chains')} (${companies_label.length}) ${hasObrigatory(
							'companies_label'
						)}`
					"
					:error="$v.companies_label.$error"
					@input="verifyLabelCompany"
					@remove="removeHeadquarter"
					:disable="disableDataWebservice || managerUser || hasCreateStore"
				>
					<q-autocomplete
						@search="fetchHeadquarters"
						:min-characters="1"
						@selected="selectHeadquarter"
						:max-results="100"
					/>
				</q-chips-input>
			</div>

			<div
				class="col-xs-12 col-sm-12"
				v-if="register_fields.store && register_store.store_as_list"
			>
				<q-select
					multiple
					filter
					autofocus-filter
					chips
					v-model="company_branches"
					ref="company_branches"
					:options="list_branches"
					:float-label="
						`${$t('stores_or_branches')} (${company_branches.length}) ${hasObrigatory(
							'company_branches'
						)}`
					"
					:error="$v.company_branches.$error"
					@input="$v.company_branches.$touch"
					:disable="disableDataWebservice || managerUser || hasCreateStore"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.store && isChevrolet">
				<q-select
					filter
					autofocus-filter
					v-model="main_company_branch"
					:options="getMainCompanyBranches"
					:float-label="`${$t('main_company_branch')}`"
					:error="$v.main_company_branch.$error"
					@input="$v.main_company_branch.$touch"
					:disable="disableDataWebservice || managerUser || hasCreateStore"
				/>
			</div>

			<div
				class="col-xs-12 col-sm-12"
				v-if="register_fields.store && !register_store.store_as_list"
			>
				<q-chips-input
					add-icon="   "
					v-model="company_branches_label"
					ref="company_branches_label"
					:float-label="
						`${$t('stores_or_branches')} (${
							company_branches_label.length
						}) ${hasObrigatory('company_branches_label')}`
					"
					@remove="removeBranch"
					:error="$v.company_branches_label.$error"
					@input="verifyLabelBranch"
					:disable="disableDataWebservice || managerUser"
				>
					<q-autocomplete
						@search="fetchBranches"
						:min-characters="3"
						@selected="selectBranch"
						:max-results="100"
					/>
				</q-chips-input>
			</div>

			<div
				class="col-xs-12 col-sm-12"
				v-if="managerCompanyBranchesGeneral.length > 0 && isVivara"
			>
				<q-field :label="$t('managers_general')"></q-field>
				<q-chip
					color="primary"
					v-for="(item, index) in managerCompanyBranchesGeneral"
					:key="index"
					>{{ item }}</q-chip
				>
			</div>

			<div
				class="col-xs-12 col-sm-12"
				v-if="managerCompanyBranchesRegional.length > 0 && isVivara"
			>
				<q-field :label="$t('managers_regional')"></q-field>
				<q-chip
					color="primary"
					v-for="(item, index) in managerCompanyBranchesRegional"
					:key="index"
					>{{ item }}</q-chip
				>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="managerCompanyBranches.length > 0">
				<q-field :label="$t('store_manager')"></q-field>
				<q-chip
					color="primary"
					v-for="(item, index) in managerCompanyBranches"
					:key="index"
					>{{ item }}</q-chip
				>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="hasCreateStore">
				<q-input
					v-model="company_branch_name"
					ref="company_branch_name"
					:float-label="`${$t('others')} ${hasObrigatory('company_branch_name')}`"
					:disable="disableDataWebservice || managerUser"
					:error="$v.company_branch_name.$error"
					@input="$v.company_branch_name.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="isChevrolet">
				<q-field
					:error="register_error"
					:error-label="register_error ? $t('register_not_unique') : ''"
				>
					<q-input
						disable
						v-model="register"
						:float-label="`${$t('register')} ${hasObrigatory('register')}`"
						:error="$v.register.$error"
						@input="$v.register.$touch"
						@blur="registerUnique()"
					/>
				</q-field>
			</div>

			<div class="col-12" v-if="register_fields.managers">
				<q-chips-input
					add-icon="   "
					v-model="managers"
					ref="managers"
					:float-label="`${$t('managers')} ${hasObrigatory('managers')}`"
					:error="$v.managers.$error"
					@input="$v.managers.$touch"
					@remove="removeManager"
					:disable="disableDataWebservice || managerUser"
				>
					<q-autocomplete
						@search="searchManager"
						@selected="selectManager"
						:min-characters="3"
						:value-field="(v) => `${v.full_name} - ${v.email}`"
					/>
				</q-chips-input>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.admission_date">
				<q-datetime
					v-model="date_hiring"
					ref="date_hiring"
					type="date"
					:error="$v.date_hiring.$error"
					@input="$v.date_hiring.$touch"
					:float-label="`${$t('admission_date')} ${hasObrigatory('date_hiring')}`"
					format="DD/MM/YYYY"
					:disable="disableDataWebservice || managerUser"
				/>
			</div>

			<div class="col-xs-12 col-sm-12 q-mt-lg" id="permissions">
				<span class="q-headline text-weight-light">{{ $t('permissions') }}</span>
			</div>
		</div>

		<q-tabs
			v-model="selected_tab"
			class="shadow-1 q-mt-md"
			color="secondary"
			@select="changeTab()"
		>
			<q-tab
				v-if="getClientName(user_client.client)"
				:name="`c${user_client.client}`"
				slot="title"
				:label="getClientName(user_client.client)"
				v-for="(user_client, index) in user_clients"
				:key="`t${index}`"
				:color="user_client.has_error ? 'negative' : ''"
			/>

			<q-tab
				:disable="managerUser"
				name="new"
				slot="title"
				icon="fal fa-plus"
				@click="addNewClient()"
				v-if="user_clients_store && user_clients_store.length > 1"
			/>

			<q-tab-pane
				:name="`c${user_client.client}`"
				v-for="(user_client, index) in user_clients"
				:key="`p${index}`"
			>
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<q-select
							filter
							autofocus-filter
							:disable="managerUser"
							v-model="user_client.profile"
							:options="getProfileOptions(user_client)"
							ref="profile"
							:float-label="`${$t('profile')}*`"
							:error="user_client.error_profile"
							@input="checkError(user_client)"
						/>
					</div>

					<div
						class="col-xs-12 col-sm-12"
						v-if="register_fields.user_type && id_user_client_country_selected == 32"
					>
						<q-select
							filter
							autofocus-filter
							:disable="managerUser"
							v-model="user_type"
							:options="getUserTypeOptions"
							:float-label="`${$t('user_type')}*`"
						/>
					</div>

					<div class="col-xs-12 col-sm-12" v-if="showIncludeUserInGroup">
						<q-select
							filter
							autofocus-filter
							:disable="managerUser"
							multiple
							v-model="user_client.user_groups"
							ref="user_groups"
							:options="
								user_client.client_user_groups ? user_client.client_user_groups : []
							"
							:float-label="`${$t('include_user_in_groups')}*`"
							@input="checkError(user_client)"
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<span class="q-caption text-faded">{{ $t('countries') }} *</span>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									:disable="managerUser"
									v-model="all_countries"
									:val="true"
									:label="$t('all')"
									@input="checkAllCountries(user_client)"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in getCountries(user_client.client)"
								:key="index"
								:class="getErrorClass(user_client.error_countries)"
							>
								<q-checkbox
									:disable="managerUser"
									keep-color
									v-model="user_client.countries"
									ref="user_client.countries"
									:val="item.id"
									:label="item.name"
									:color="getErrorColor(user_client.error_countries)"
									@input="checkError(user_client)"
								/>
							</div>
						</div>
					</div>

					<div class="col-xs-12 col-sm-12 q-mt-md">
						<span class="q-caption text-faded">{{ $t('areas') }} *</span>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									:disable="managerUser || !user_client.profile"
									v-model="all_areas"
									:val="true"
									:label="$t('all_f')"
									@input="checkAllAreas(user_client)"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in getAreas(user_client.client)"
								:key="index"
								:class="getErrorClass(user_client.error_area)"
								v-if="!register_store.store_as_list"
							>
								<q-checkbox
									:disable="managerUser"
									keep-color
									v-model="user_client.areas"
									:val="item.value"
									:label="item.label"
									@input="checkError(user_client)"
								/>
							</div>

							<div class="col-xs-12 col-sm-12" v-if="register_store.store_as_list">
								<select-multiple
									label="areas"
									:filters="user_client.areas"
									:options="getAreas(user_client.client)"
									@on_value="user_client.areas = $event"
								/>
							</div>

							<div class="col-12" v-if="showMainAreas">
								<q-select
									autofocus-filter
									filter
									v-model="user_client.main_areas"
									ref="user_client.main_areas"
									:options="getMainAreas(user_client)"
									:float-label="`${$t('main_area')}*`"
									:error="user_client.error_main_areas"
									:disable="managerUser"
								/>
							</div>
						</div>
					</div>

					<div
						class="col-xs-12 col-sm-12 q-mt-md"
						v-if="hasControlPanelAccess(user_client)"
					>
						<span class="q-caption text-faded">{{ $t('access') }}</span>

						<div class="row gutter-sm q-mb-md">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									:disable="managerUser"
									v-model="all_modules"
									:val="true"
									:label="$t('all')"
									@input="checkAllModules(user_client)"
								/>
							</div>
						</div>

						<span
							class="q-caption text-faded"
							v-if="controlPannel(user_client.client).length > 0"
							>{{ $t('control_pannel') }}</span
						>

						<div
							class="row gutter-sm q-mb-md"
							v-if="controlPannel(user_client.client).length > 0"
						>
							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in controlPannel(user_client.client)"
								:key="index"
							>
								<q-checkbox
									:disable="managerUser"
									keep-color
									v-model="user_client.module_items"
									:val="item.id"
									:label="$t(item.slug)"
								/>
								<q-tooltip>
									{{ $t(`${item.slug}_tooltip`) }}
								</q-tooltip>
							</div>
						</div>

						<span
							class="q-caption text-faded"
							v-if="trainingManager(user_client.client).length > 0"
							>{{ $t('presential') }}</span
						>

						<div
							class="row gutter-sm q-mb-md"
							v-if="trainingManager(user_client.client).length > 0"
						>
							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in trainingManager(user_client.client)"
								:key="index"
							>
								<q-checkbox
									:disable="managerUser"
									keep-color
									v-model="user_client.module_items"
									:val="item.id"
									:label="$t(item.slug)"
								/>
								<q-tooltip>
									{{ $t(`${item.slug}_tooltip`) }}
								</q-tooltip>
							</div>
						</div>

						<span
							class="q-caption text-faded"
							v-if="rewards(user_client.client).length > 0"
							>{{ $t('rewards') }}</span
						>

						<div
							class="row gutter-sm q-mb-md"
							v-if="rewards(user_client.client).length > 0"
						>
							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in rewards(user_client.client)"
								:key="index"
							>
								<q-checkbox
									:disable="managerUser"
									keep-color
									v-model="user_client.module_items"
									:val="item.id"
									:label="$t(item.slug)"
								/>
								<q-tooltip>
									{{ $t(`${item.slug}_tooltip`) }}
								</q-tooltip>
							</div>
						</div>
					</div>

					<div class="col-xs-12 col-sm-12 q-mt-lg">
						<div class="row reverse">
							<div class="col-xs-12 col-sm-3">
								<q-btn
									:disable="managerUser"
									class="fit"
									color="negative"
									icon="fal fa-trash"
									:label="$t('remove_permission')"
									@click="confirmRemoveClient(user_client, index)"
								/>
							</div>
						</div>
					</div>
				</div>
			</q-tab-pane>

			<q-tab-pane name="new" class="q-py-xl" />
		</q-tabs>

		<div class="col-xs-12 col-sm-12 q-mt-md" v-if="this.id != 0">
			<q-input
				v-model="accept_terms_date"
				:disable="true"
				:float-label="$t('accept_terms_date')"
			/>
		</div>

		<div class="row gutter-xs q-mt-md">
			<div class="col-xs-12 col-sm-12">
				<span class="q-headline text-weight-light">{{ $t('status') }}</span>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="status"
					:options="optionsStatus"
					:float-label="$t('status')"
					:disable="disableDataWebservice"
					:readonly="disableDataWebservice"
				/>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_users' })"
				/>
			</div>

			<div
				class="col-xs-12 col-sm-3"
				v-if="register_fields.default_password != '' && id != 0 && this.checkGeneratedEmail"
			>
				<q-btn
					color="warning"
					class="full-width"
					icon="far fa-key"
					:label="$t('reset_user_password')"
					@click="resetUserPassword()"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					:disable="managerUser"
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div v-if="showAnonymizeUserData && status === 13" class="col-xs-12 col-sm-3">
				<q-btn
					color="warning"
					class="full-width"
					:label="$t('user_data_exclusion')"
					icon="far fa-exclamation-circle"
					@click="show_anonymize_dialog = true"
				/>
			</div>
			<div v-if="is_superuser || user_login_as_user" class="col-xs-12 col-sm-3">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('login_as_user')"
					icon="far fa-eye"
					@click="loginAsUser()"
				/>
			</div>
		</div>

		<q-dialog v-model="show_confirmation_remove" @ok="removeClient()">
			<span slot="title">{{ $t('remove_permission') }}</span>

			<span slot="message"
				>{{ $t('do_you_really_want_to_remove_the_access_for_the_client') }}
				<strong>{{ getClientName(confirm_remove_client.client) }}</strong
				>? {{ $t('all_permissions_will_be_lost') }}!</span
			>

			<template slot="buttons" slot-scope="props">
				<q-btn
					color="primary"
					:label="$t('cancel')"
					@click="show_confirmation_remove = false"
				/>
				<q-btn color="negative" :label="$t('remove')" @click="removeClient()" />
			</template>
		</q-dialog>

		<q-dialog v-model="show_add_client" @ok="addClient()" no-backdrop-dismiss no-esc-dismiss>
			<span slot="title">{{ $t('add_client') }}</span>

			<div slot="body">
				<q-select v-model="new_client" :options="clientList" />
			</div>

			<template slot="buttons" slot-scope="props">
				<q-btn color="tertiary" :label="$t('cancel')" @click="cancelAddClient()" />
				<q-btn color="secondary" :label="$t('add')" @click="addClient()" />
			</template>
		</q-dialog>

		<q-dialog
			v-model="show_anonymize_dialog"
			@ok="requestAnonymizeUserData(false)"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<span slot="title">{{ $t('delete_user_data_confirmation') }}</span>

			<div slot="body">
				<p>{{ $t('delete_user_data_warning_message') }}</p>
				<p>{{ $t('delete_user_data_mistake_message') }}</p>
				<p>{{ $t('delete_user_data_danger_message') }}</p>
			</div>

			<template slot="buttons" slot-scope="props">
				<div style="display: flex; flex-direction: column; align-items: flex-end;">
					<div style="width: 100%; display: flex; justify-content: flex-end;">
								<q-btn
										color="tertiary"
										:label="$t('cancel')"
										@click="$router.push({ name: 'admin_users' })"
								/>
								<q-btn
										color="warning"
										:label="$t('reject')"
										@click="requestAnonymizeUserData(false)"
										:disabled="!is_admin && !is_superuser"
								/>
								<q-btn
										color="negative"
										:label="$t('confirm')"
										@click="requestAnonymizeUserData(true)"
										:disabled="!is_admin && !is_superuser"
								/>
						</div>
						<span v-if="!is_admin && !is_superuser" style="margin-top: 8px; align-self: flex-end; color: red; font-size: 12px">
							{{ $t('anonymize_permission_text_info') }}
						</span>
				</div>
		</template>


		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
import { required, requiredIf, email } from 'vuelidate/lib/validators'
import { OpenStreetMapProvider } from 'leaflet-geosearch'
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import SelectMultiple from 'components/utils/SelectMultiple'

const provider = new OpenStreetMapProvider({
	params: {
		addressdetails: '1',
	},
})
const { formatDate } = date

export default {
	i18nOptions: { namespaces: ['custom', 'users', 'common', 'admin', 'rewards', 'organization'] },

	data() {
		return {
			accept_terms_date: '',
			address: '',
			address_id: '',
			all_areas: false,
			all_countries: false,
			all_modules: false,
			areasClient: [],
			birthday: null,
			branch_regionals: [],
			city: '',
			companies: [],
			companies_label: [],
			company_branch_manager: [],
			company_branch_name: '',
			company_branches: [],
			company_branches_label: [],
			complement: '',
			confirm_remove_client: [],
			confirm_remove_index: '',
			date_hiring: '',
			document: '',
			document_error: false,
			email: '',
			email_error: false,
			email_error_prefix: false,
			full_name: '',
			gender: null,
			handicap_description: '',
			id_user: null,
			is_admin: false,
			is_handicapped: false,
			lat: '',
			list_branches: [],
			long: '',
			managers: [],
			managers_full_data: [],
			managers: [],
			marital_status: '',
			neighborhood: '',
			new_client: null,
			nick_name: '',
			number: '',
			number_of_children: 0,
			option_address: [],
			phone_commercial: {},
			phone_mobile: {},
			phone_residential: {},
			phone_whatsapp: {},
			postal_code: '',
			register: '',
			register_2: '',
			register_2_error: false,
			register_error: false,
			reset_user_password: false,
			selected_tab: '',
			send_welcome_email: false,
			show_add_client: false,
			show_confirmation_remove: false,
			show_maps_alert: false,
			show_other_branches: false,
			state: '',
			status: 1,
			user_clients: [],
			current_client: '',
			user_type: '',
			timezone: '',
			main_company_branch: '',
			user_login_as_user: false,
			user_country: null,
			document_invalid: false,
			show_anonymize_dialog: false,
		}
	},

	components: {
		'select-multiple': SelectMultiple,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			branch_regional: 'registration_lists/branch_regional',
			clients: 'params/clients',
			countries: 'registration_lists/countries',
			genders: 'registration_lists/genders',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			id_user_client_selected: 'common/id_user_client_selected',
			id_user_language_selected: 'common/id_user_language_selected',
			is_superuser: 'common/is_superuser',
			marital_statuss: 'registration_lists/marital_status',
			phone_types: 'registration_lists/phone_types',
			profiles: 'registration_lists/profiles',
			register_fields: 'params/register_fields',
			register_store: 'params/register',
			user_client_module_items: 'common/user_client_module_items',
			user_clients_store: 'common/user_clients',
			user_module_items: 'common/user_module_items',
			user_types: 'registration_lists/user_types',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},
		optionsStatus() {
			const status = [
				{ label: this.$t('active'), value: 1 },
				{ label: this.$t('inactive'), value: 2 },
				{ label: this.$t('awaiting_validation'), value: 4 },
				{ label: this.$t('self_registered'), value: 6 },
				{ label: this.$t('blocked'), value: 8 },
				{ label: this.$t('inactive_license'), value: 9 },
				{ label: this.$t('inactive_former_employee'), value: 10 },
				{ label: this.$t('inactive_compliance'), value: 11 },
				{ label: this.$t('inactive_vacation'), value: 12 },
				{ label: this.$t('inactive_auto'), value: 15, disable: true },
				{ label: this.$t('complete_registration'), value: 16 },
			]
			if (!this.managerUserDelete) status.push({ label: this.$t('deleted'), value: 3 })
			return status
		},

		checkGeneratedEmail() {
			const url = process.env.SITE_URL.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split(
				'/'
			)[0]
			const email = `${this.document}@${url}`
			return this.email == email
		},

		addressRequired() {
			if (this.register_fields.address_fields) {
				if (this.address || this.city || this.state) return true
				else return false
			} else return false
		},

		clientList() {
			const clients = []

			this.user_clients_store.forEach((client) => {
				clients.push({
					value: client.id,
					label: client.name,
					disable: this.user_clients.find((obj) => obj.client === client.id),
				})
			})

			return clients
		},

		disableDataWebservice() {
			if (this.status === 5 || this.status === 7 || this.status === 13) return true
			else return false
		},

		managerUser() {
			const obj = this.user_client_module_items.find(
				(obj) => obj.id === this.id_user_client_selected
			)
			if (obj) {
				const item = obj.module_items.find((obj) => obj.slug === 'users_manage')
				if (item.permission) return false
				else return true
			}
			return true
		},

		managerUserDelete() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'users_delete_manage'
				})
				if (item && item.permission) return false
				else return true
			}
			return true
		},

		keyGooogleMaps() {
			return process.env.KEY_GOOGLE_API
		},

		getUserGenders() {
			if (this.genders) {
				return this.genders.map((gender) => {
					return {
						...gender,
						label: this.$t(gender.label),
					}
				})
			}
		},

		hasCreateStore() {
			let obj = this.company_branches.find((obj) => obj.value == -1)
			if (obj) {
				return true
			} else {
				this.company_branch_name = ''
				return false
			}
		},

		managerCompanyBranches() {
			let item = []
			if (this.company_branches.length > 0) {
				if (this.register_store.store_as_list) {
					this.list_branches.map((e) => {
						this.company_branches.map((x) => {
							if (e.id == x) {
								e.managers.map((y) => {
									item.push(`${y.full_name} - ${y.email} - ${y.profile_name}`)
								})
							}
						})
					})
				} else {
					this.company_branches.map((c) => {
						if (c.managers_list && c.managers_list.length > 0) {
							c.managers_list.map((x) => {
								item.push(`${x.full_name} - ${x.email} - ${x.profile_name}`)
							})
						}
					})
				}
			}
			return item
		},

		managerCompanyBranchesRegional() {
			let item = []
			if (this.company_branches.length > 0) {
				if (this.register_store.store_as_list) {
					this.list_branches.map((e) => {
						this.company_branches.map((x) => {
							if (e.id == x) {
								e.managers_regional.map((y) => {
									item.push(`${y.full_name} - ${y.email} - ${y.profile_name}`)
								})
							}
						})
					})
				} else {
					this.company_branches.map((c) => {
						if (c.managers_regional_list && c.managers_regional_list.length > 0) {
							c.managers_regional_list.map((x) => {
								item.push(`${x.full_name} - ${x.email} - ${x.profile_name}`)
							})
						}
					})
				}
			}
			return item
		},

		managerCompanyBranchesGeneral() {
			let item = []
			if (this.company_branches.length > 0) {
				if (this.register_store.store_as_list) {
					this.list_branches.map((e) => {
						this.company_branches.map((x) => {
							if (e.id == x) {
								e.managers_general.map((y) => {
									item.push(`${y.full_name} - ${y.email} - ${y.profile_name}`)
								})
							}
						})
					})
				} else {
					this.company_branches.map((c) => {
						if (c.managers_general_list && c.managers_general_list.length > 0) {
							c.managers_general_list.map((x) => {
								item.push(`${x.full_name} - ${x.email} - ${x.profile_name}`)
							})
						}
					})
				}
			}
			return item
		},

		showMainAreas() {
			if (process.env.PROJECT_FOLDER === 'universidadechevrolet') return true
			else return false
		},

		showIncludeUserInGroup() {
			let obj = this.user_client_module_items.find(
				(obj) => obj.id === this.id_user_client_selected
			)
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'users_groups'
				})
				if (item) return item.permission
			}
			return false
		},

		showPhones() {
			if (
				this.register_fields.mobile_phone_fields ||
				this.register_fields.whatsapp_phone_fields ||
				this.register_fields.residential_phone_fields ||
				this.register_fields.commercial_phone_fields
			)
				return true
			else return false
		},

		messageErrorEmail() {
			if (this.email_error || this.email_error_prefix) {
				if (this.email_error_prefix) return this.$t('invalid_email_address')
				else return this.$t('email_not_unique')
			} else {
				return ''
			}
		},

		documentIsCPF() {
			if (
				process.env.PROJECT_FOLDER === 'universidadechevrolet' &&
				this.id_user_client_country_selected === 32 &&
				this.id_user_language_selected == 1
			)
				return 'cpf'
			else if (
				(process.env.PROJECT_FOLDER === 'siemens' ||
					process.env.PROJECT_FOLDER === 'academiacoral') &&
				this.id_user_language_selected == 1
			)
				return 'document_cpf'
			return 'document'
		},

		getUserTypeOptions() {
			return this.user_types ? this.user_types : []
		},

		getMainCompanyBranches() {
			let main_branches = this.list_branches.filter((e) =>
				this.company_branches.includes(e.value)
			)
			if (main_branches.length === 0) this.main_company_branch = ''
			return main_branches
		},

		documentError() {
			if (this.document_error && !this.document_invalid) return 'document_not_unique'
			else if (this.document_invalid) return 'invalid_document'
		},

		userCountryObjected() {
			return this.countries.find((obj) => obj.value === this.user_country)
		},

		showAnonymizeUserData() {
			// if (!this.is_admin) return false
			const client = this.clients.find((c) => c.id === this.id_user_client_selected)
			if (client) {
				return client.has_account_anonymization
			}
			return false
		},

		isChevrolet() {
			return process.env.PROJECT_FOLDER === 'universidadechevrolet'
		},

		isVivara() {
			return process.env.PROJECT_FOLDER === 'mundovivara'
		},
	},

	methods: {
		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_users/users/${this.id}/`, this.getHeader)
				.then((response) => {
					this.full_name = response.data.full_name
					this.nick_name = response.data.nick_name
					this.email = response.data.email
					this.status = response.data.status

					if (this.status === 4) this.send_welcome_email = true

					if (response.data.user_data) {
						this.birthday = response.data.birthday
							? moment(response.data.birthday)
									.tz('Etc/GMT')
									.format('YYYY-MM-DDTHH:mm:ss.SSS')
							: null
						this.accept_terms_date = response.data.accept_terms_date
							? moment(response.data.accept_terms_date)
									.tz(this.timezone)
									.format('DD/MM/YYYY - HH:mm')
							: null

						if (this.countries.length == 1) this.user_country = this.countries[0].value
						else this.user_country = response.data.user_data.document_country_id

						this.register = response.data.user_data.register
						this.marital_status = response.data.user_data.marital_status
						this.number_of_children = response.data.user_data.number_of_children
						this.gender = response.data.user_data.gender
						this.companies = response.data.user_data.companies
						setTimeout(() => {
							this.document = response.data.user_data.document
						}, 1000)
						this.is_handicapped = response.data.user_data.is_handicapped
						this.handicap_description = response.data.user_data.handicap_description
						this.register_2 = response.data.user_data.register_2

						if (this.register_store.store_as_list) {
							this.company_branches = response.data.user_data.company_branches.map(
								(e) => {
									return e.id
								}
							)
						} else this.company_branches = response.data.user_data.company_branches

						this.main_company_branch = response.data.user_data.main_company_branch

						this.user_type = response.data.user_data.user_type_id
							? response.data.user_data.user_type_id
							: ''

						this.date_hiring = response.data.user_data.date_hiring
							? moment(response.data.user_data.date_hiring)
									.tz('Etc/GMT')
									.format('YYYY-MM-DDTHH:mm:ss.SSS')
							: null

						response.data.user_data.managers.forEach((manager) => {
							this.managers.push(`${manager.full_name} - ${manager.email}`)
							this.managers_full_data.push(manager)
						})

						response.data.user_data.branch_regional.forEach((regional) => {
							this.branch_regionals.push(regional)
						})

						response.data.user_data.companies.forEach((company) => {
							this.companies_label.push(company.name)
						})

						response.data.user_data.company_branches.forEach((company_branch) => {
							this.company_branches_label.push(
								`${company_branch.name} ${company_branch.document ? '- ' : ''} ${
									company_branch.document ? company_branch.document : ''
								} ${company_branch.register ? '- ' : ''} ${
									company_branch.register ? company_branch.register : ''
								}`
							)
						})

						response.data.user_data.phones.forEach((phone) => {
							switch (phone.phone_type) {
								case 3:
									this.phone_mobile = { ...phone }
									break
								case 1:
									this.phone_commercial = { ...phone }
									break
								case 2:
									this.phone_residential = { ...phone }
									break
								case 4:
									this.phone_whatsapp = { ...phone }
									break
							}
						})

						if (response.data.user_data.addresses) {
							response.data.user_data.addresses.forEach((address) => {
								this.address_id = address.id
								this.address = address.address
								this.postal_code = address.postal_code
								this.city = address.city
								this.state = address.state
								this.lat = address.lat
								this.long = address.long
								this.neighborhood = address.neighborhood
								this.number = address.number
								this.complement = address.complement
								this.country = address.country
							})
						}
					}

					this.user_clients = response.data.user_clients
					this.selected_tab = `c${response.data.user_clients[0].client}`

					this.user_clients.forEach((client) => {
						client.main_areas =
							client.main_areas && client.main_areas.length > 0
								? client.main_areas[0]
								: []

						const obj = this.user_clients_store.find((obj) => obj.id === client.client)
						if (obj) {
							client.client_profiles = obj.client_profiles
							client.client_user_groups = obj.client_user_groups
						}
					})

					//removido para enviar o payload completo. #4193929291
					// this.user_clients = this.user_clients.filter((client) => {
					// 	if (this.user_clients_store) {
					// 		const obj = this.user_clients_store.find(
					// 			(obj) => obj.id === client.client
					// 		)
					// 		return obj ? true : false
					// 	}
					// })

					this.canLoginAsUser(this.user_clients)

					if (this.register_store.store_as_list) this.getBranchRegional()

					if (this.showAnonymizeUserData && this.status === 13) {
						this.show_anonymize_dialog = true
					}

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
					if (e.response.status === 404) {
						this.$q.notify({
							message: this.$t('not_allowed_or_page_not_found'),
							type: 'negative',
							icon: 'fal fa-ban',
						})

						this.$router.push({ name: 'admin_users' })
					}
				})
		},

		saveData() {
			if (this.managerUser) return

			let has_errors = false
			this.$v.$touch()
			this.validateEmail()

			if (this.user_clients.length > 0) {
				this.user_clients.forEach((client) => {
					client.has_error = false

					if (client.areas.length == 0) {
						has_errors = true
						client.error_area = true
						client.has_error = true
					} else client.error_area = false

					if (client.countries.length == 0) {
						has_errors = true
						client.error_countries = true
						client.has_error = true
					} else client.error_countries = false

					if (!client.profile) {
						has_errors = true
						client.error_profile = true
						client.has_error = true
					} else client.error_profile = false

					if (this.showMainAreas) {
						if (client.main_areas.length === 0) {
							has_errors = true
							client.error_main_areas = true
							client.has_error = true
						} else client.error_main_areas = false
					} else client.error_main_areas = false
				})
			} else {
				has_errors = true
			}

			if (
				(!this.register_fields.email_fields && this.email === '') ||
				(!this.register_fields.email_fields_mandatory &&
					this.register_fields.default_password != '' &&
					this.email === '' &&
					this.document != '')
			) {
				let url = process.env.SITE_URL.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split(
					'/'
				)[0]

				this.email = `${this.document}@${url}`
			}

			if (!this.$v.$error && !has_errors && !this.email_error_prefix) {
				this.$q.loading.show()

				let user_clients = []
				let phones = []
				let user_data = []

				let addresses = [
					{
						id: this.address_id,
						address: this.address,
						city: this.city,
						complement: this.complement,
						lat: this.lat,
						long: this.long,
						neighborhood: this.neighborhood,
						number: this.number,
						postal_code: this.postal_code,
						state: this.state,
						country: this.country,
					},
				]

				this.user_clients.forEach((user_client) => {
					user_clients.push({
						areas: user_client.areas,
						client: user_client.client,
						countries: user_client.countries,
						module_items: user_client.module_items,
						profile: user_client.profile,
						user_groups: user_client.user_groups,
						main_areas: user_client.main_areas
							? Array.isArray(user_client.main_areas)
								? user_client.main_areas
								: [user_client.main_areas]
							: [],
					})
				})

				if (this.phone_mobile.phone) {
					phones.push({
						...this.phone_mobile,
						phone_type: 3,
						area_code: '',
						country_code: '',
					})
				}
				if (this.phone_residential.phone) {
					phones.push({
						...this.phone_residential,
						phone_type: 2,
						area_code: '',
						country_code: '',
					})
				}
				if (this.phone_commercial.phone) {
					phones.push({
						...this.phone_commercial,
						phone_type: 1,
						area_code: '',
						country_code: '',
					})
				}
				if (this.phone_whatsapp.phone) {
					phones.push({
						...this.phone_whatsapp,
						phone_type: 4,
						area_code: '',
						country_code: '',
					})
				}

				user_data = {
					addresses: addresses,
					birthday: date.formatDate(this.birthday, 'YYYY-MM-DD'),
					companies: this.companies,
					document:
						this.register_store.use_document_mask && this.document
							? this.$mask.removeMask(this.document)
							: this.document,
					document_country_id: this.user_country,
					gender: this.gender,
					phones: phones,
					register: this.register,
					managers: this.managers_full_data,
					date_hiring: date.formatDate(this.date_hiring, 'YYYY-MM-DD'),
					is_handicapped: this.is_handicapped,
					handicap_description: this.handicap_description,
					register_2: this.register_2,
					main_company_branch: this.main_company_branch,
				}

				if (this.register_fields.user_type && this.id_user_client_country_selected == 32) {
					user_data.user_type_id = this.user_type
				}

				if (this.register_store.store_as_list) {
					let branches = []
					if (this.company_branches.length > 0) {
						branches = this.company_branches.map((e) => ({ id: e }))
						// this.list_branches.map((e) => {
						// 	this.company_branches.map((x) => {
						// 		if (e.id == x) branches.push(e)
						// 	})
						// })
					}

					user_data.company_branches = branches
				} else
					user_data.company_branches = this.company_branch_name
						? -1
						: this.company_branches

				if (this.number_of_children) user_data.number_of_children = this.number_of_children
				if (this.marital_status) user_data.marital_status = this.marital_status

				if (!this.company_branch_name) user_data.branch_regional = this.branch_regionals
				if (this.company_branch_name)
					user_data.company_branch_name = this.company_branch_name
				if (this.company_branch_name)
					user_data.store_country = this.id_user_client_country_selected

				let data = {
					email: this.email,
					full_name: this.full_name,
					status: this.status,
					user_data: user_data,
					user_clients: user_clients,
				}

				if (this.nick_name) data.nick_name = this.nick_name

				if (this.id != 0) {
					if (this.checkGeneratedEmail && this.reset_user_password)
						data.password_to_change = this.register_store.default_password
					this.$axios
						.put(`/admin_users/users/${this.id}/`, data, this.getHeader)
						.then(() => {
							if (this.send_welcome_email && this.status === 1) {
								this.sendWelcomeEmail()
							} else if (this.reset_user_password) {
								this.reset_user_password = false
								this.$q.loading.hide()

								this.$q.notify({
									message: this.$t('user_password_changed_to_default'),
									type: 'positive',
									icon: 'fal fa-key',
									position: 'top',
								})
							} else if (this.isChevrolet) {
								this.loadK011()
							} else {
								this.$q.loading.hide()
								this.$router.push({ name: 'admin_users' })
							}
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else {
					if (this.checkGeneratedEmail)
						data.password_to_change = this.register_store.default_password
					this.$axios
						.post(`/admin_users/users/`, data, this.getHeader)
						.then((response) => {
							this.id = response.data.id

							if (this.isChevrolet) {
								this.loadK011()
							} else {
								this.$q.loading.hide()
								this.$router.push({ name: 'admin_users' })
							}
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)

				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		loadK011() {
			let data_k011 = {
				user_id: this.id,
			}

			this.$axios
				.post(`/webservices/universidadechevrolet/load_k011/`, data_k011, this.getHeader)
				.then(() => {})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
					this.$router.push({ name: 'admin_users' })
				})
		},

		sendWelcomeEmail() {
			let fd = new FormData()
			fd.append('user_id', this.id)

			this.$axios
				.post(`/admin_users/users/welcome_email/`, fd, this.getHeader)
				.then(() => {
					if (this.isChevrolet) {
						this.loadK011()
					} else {
						this.$q.loading.hide()
						this.$router.push({ name: 'admin_users' })
					}
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		checkError(client) {
			client.has_error = false

			if (client.areas.length == 0) {
				client.error_area = true
				client.has_error = true
			} else client.error_area = false

			if (client.countries.length == 0) {
				client.error_countries = true
				client.has_error = true
			} else client.error_countries = false

			if (!client.profile) {
				client.error_profile = true
				client.has_error = true
			} else {
				client.error_profile = false
			}
		},

		addNewClient() {
			if (this.managerUser) return
			if (!this.disableDataWebservice) this.show_add_client = true
		},

		addClient() {
			let obj = this.user_clients_store.find((obj) => obj.id === this.new_client)
			if (obj) {
				let client_countries = []

				obj.countries.forEach((country) => {
					client_countries.push({
						value: country.id,
						label: country.name,
					})
				})

				this.user_clients.push({
					client: this.new_client,
					areas: [],
					countries: [],
					module_items: [],
					user_groups: [],
					profile: '',
					client_areas: obj.client_areas,
					client_countries: client_countries,
					client_profiles: obj.client_profiles,
					client_user_groups: obj.client_user_groups,
					error_area: false,
					error_countries: false,
					error_profile: false,
					has_error: false,
					main_areas: [],
				})
				this.selected_tab = `c${this.user_clients[this.user_clients.length - 1].client}`
				this.show_add_client = false
				this.new_client = null
			}
		},

		cancelAddClient() {
			if (this.user_clients.length > 0) this.selected_tab = `c${this.user_clients[0].client}`
			else this.selected_tab = null

			this.show_add_client = false
			this.new_client = null
		},

		confirmRemoveClient(client, index) {
			this.confirm_remove_client = client
			this.confirm_remove_index = index
			this.show_confirmation_remove = true
		},

		removeClient() {
			if (this.user_clients.length > 1) {
				this.user_clients.splice(this.confirm_remove_index, 1)
				this.selected_tab = `c${this.user_clients[0].client}`

				this.$q.notify({
					message: this.$t('client_removed_save_the_changes'),
					type: 'positive',
					icon: 'fal fa-check',
				})
			} else {
				this.$q.notify({
					message: this.$t('the_user_need_at_least_one_client'),
					type: 'negative',
					icon: 'fal fa-ban',
				})
			}
			this.show_confirmation_remove = false

			let options = {
				easing: 'ease-in',
				force: true,
				cancelable: false,
				offset: -50,
			}

			this.$scrollTo('#permissions', 100, options)
		},

		getAddress(terms, done) {
			provider.search({ query: terms }).then((result) => {
				done(result)
			})
		},

		selectedAddress(item) {
			this.$nextTick(() => (this.address = item.raw.address.road))

			this.postal_code = item.raw.address.postcode
			this.city = item.raw.address.city
			this.state = item.raw.address.state
			this.lat = item.raw.lat
			this.long = item.raw.lon
			this.country = item.raw.address.country

			if (item.raw.address.neighbourhood) {
				this.neighborhood = item.raw.address.neighbourhood
			} else if (item.raw.address.suburb) {
				this.neighborhood = item.raw.address.suburb
			} else if (item.raw.address.city_district) {
				this.neighborhood = item.raw.address.city_district
			}

			this.$nextTick(() => this.$refs.number.$el.focus())
		},

		getAddressGoogleMaps(terms, done) {
			this.$axios
				.get(
					`https://maps.googleapis.com/maps/api/geocode/json?address=${terms}&key=${
						this.keyGooogleMaps
					}`
				)
				.then((response) => {
					if (response.data.results.length > 0) {
						this.option_address = response.data.results
						const mapped = response.data.results.map((e) => ({
							label: e.formatted_address,
							value: e.formatted_address,
							place_id: e.place_id,
						}))
						done(mapped)
					} else {
						this.show_maps_alert = true
					}
				})
				.catch((e) => {
					console.log(e)
				})
		},

		selectedAddressGoogleMaps(item) {
			this.show_maps_alert = false

			this.address = ''
			this.postal_code = ''
			this.city = ''
			this.state = ''
			this.lat = ''
			this.long = ''
			this.country = ''
			this.neighborhood = ''
			this.number = ''

			this.option_address.map((x) => {
				if (x.place_id === item.place_id) {
					this.lat = x.geometry.location.lat
					this.long = x.geometry.location.lng
					x.address_components.map((e) => {
						switch (e.types[0]) {
							case 'route':
								this.address = e.long_name
								break
							case 'political':
							case 'administrative_area_level_4':
								this.neighborhood = e.long_name
								break
							case 'street_number':
								this.number = e.long_name
								break
							case 'postal_code':
								this.postal_code = e.long_name
								break
							case 'administrative_area_level_2':
								this.city = e.long_name
								break
							case 'country':
								this.country = e.long_name
								break
							case 'administrative_area_level_1':
								this.state = e.long_name
								break
						}
					})
				}
			})
		},

		documentUnique() {
			if (this.document) {
				let document = this.$mask.removeMask(this.document)
				let fd = new FormData()
				fd.append('document', document)
				fd.append('id_user', this.id)

				this.$axios
					.post(`/admin_users/users/check_document_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) {
							this.document_error = true
							this.id_user = response.data.id_user
						} else this.document_error = false

						if (this.user_country != '') {
							this.document_invalid = this.$mask.validateDocument(
								document,
								this.userCountryObjected.code,
								this.register_store.use_document_mask
							)
						}
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.document_error = false
		},

		registerUnique() {
			if (this.register) {
				let fd = new FormData()
				fd.append('register', this.register)
				fd.append('id_user', this.id)

				this.$axios
					.post(`/admin_users/users/check_register_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) {
							this.register_error = true
							this.id_user = response.data.id_user
						} else this.register_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.register_error = false
		},

		register2Unique() {
			if (this.register_2) {
				let fd = new FormData()
				fd.append('register_2', this.register_2)
				fd.append('id_user', this.id)

				this.$axios
					.post(`/admin_users/users/check_register_2_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) {
							this.register_2_error = true
							this.id_user = response.data.id_user
						} else this.register_2_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.register_2_error = false
		},

		validateEmail() {
			this.emailUnique()
			if (this.isChevrolet) this.validateEmailPrefix()
		},

		emailUnique() {
			if (this.email) {
				let fd = new FormData()
				fd.append('email', this.email)
				fd.append('id_user', this.id)

				this.$axios
					.post(`/admin_users/users/check_email_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) {
							this.email_error = true
							this.id_user = response.data.id_user
						} else this.email_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.email_error = false
		},

		validateEmailPrefix() {
			let emails = [
				'gmail.com',
				'bol.com.br',
				'hotmail.com',
				'hotmail.com.br',
				'outlook.com',
				'outlook.com.br',
				'uol.com.br',
				'aol.com',
				'me.com',
				'globo.com',
				'globo.com',
				'yahoo.com',
				'live.com',
				'bing.com',
				'tutanota.com',
				'fastmail.com',
				'zoho.com',
			]

			let prefix = this.email.split('@')

			let obj = emails.find((e) => e == prefix[1].toLowerCase())

			return obj ? (this.email_error_prefix = true) : (this.email_error_prefix = false)
		},

		getClientName(id) {
			if (this.user_clients_store) {
				let obj = this.user_clients_store.find((obj) => obj.id === id)
				return obj ? obj.name : ''
			}
		},

		checkAllCountries(client) {
			let selected_client = this.user_clients_store.find((obj) => obj.id === client.client)
			selected_client.countries.map(({ id }) => {
				client.countries = client.countries.filter((e) => e !== id)
			})

			if (this.all_countries) {
				let selected_client = this.user_clients_store.find(
					(obj) => obj.id === client.client
				)
				if (selected_client) {
					selected_client.countries.forEach((element) => {
						client.countries.push(element.id)
					})
				}
			}

			this.checkError(client)
		},

		checkAllAreas(client) {
			const selected_client = this.user_clients_store.find((obj) => obj.id === client.client)
			selected_client.client_areas.map(({ value }) => {
				client.areas = client.areas.filter((e) => e !== value)
			})

			if (this.all_areas) {
				if (selected_client) {
					selected_client.client_areas.forEach((element) => {
						client.areas.push(element.value)
					})
				}
			} else {
				const selected_client = this.user_clients_store.find(
					(obj) => obj.id === client.client
				)
				if (selected_client && selected_client.client_areas) {
					const totalAreasUser = client.areas.filter((selectedArea) => {
						const areasClient = selected_client.client_areas.find(
							(a) => a.value == selectedArea
						)
						if (!areasClient) return selectedArea
					})
					client.areas = totalAreasUser
				}
			}

			this.checkError(client)
		},

		checkAllModules(client) {
			const obj = this.user_client_module_items.find((obj) => obj.id === client.client)
			obj.module_items.map(({ id }) => {
				client.module_items = client.module_items.filter((e) => e !== id)
			})

			if (this.all_modules) {
				if (obj) {
					const items = obj.module_items.filter((i) => i.permission)
					items.forEach((element) => {
						client.module_items.push(element.id)
					})
				}
			}
		},

		changeTab() {
			this.all_areas = false
			this.all_countries = false
			this.all_modules = false
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		getErrorColor(state) {
			return state ? 'negative' : ''
		},

		getErrorClass(state) {
			return state ? 'checkbox-error' : ''
		},

		fetchHeadquarters(terms, done) {
			this.$axios
				.get(`/admin_companies/companies/get_companies/?search=${terms}`, this.getHeader)
				.then((response) => {
					let headquarters = []

					response.data.forEach((element) => {
						headquarters.push({
							value: element.id,
							label: `${element.name} / ${this.$t(element.company_type_label)} ${
								element.document && element.register
									? `${element.document} - ${element.register}`
									: element.document
									? element.document
									: element.register
									? element.register
									: ''
							}`,
							address: element.address,
							client: element.client,
							company_type: element.company_type,
							company_type_label: element.company_type_label,
							created_date: element.created_date,
							document: element.document,
							fantasy: element.fantasy,
							id: element.id,
							name: element.name,
							qty_branches: element.qty_branches,
							status: element.status,
							updated_date: element.updated_date,
						})
					})

					done(headquarters)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		verifyLabelCompany() {
			this.$v.companies_label.$touch
			if (this.companies.lenght !== this.companies_label) {
				this.companies_label.pop()
			}
		},

		selectHeadquarter(item) {
			let obj = this.companies.find((obj) => obj.id === item.id)
			if (!obj) {
				this.companies.push(item)
			}

			this.companies_label = []
			this.companies.forEach((element) => {
				this.companies_label.push(element.name)
			})

			if (this.register_store.store_as_list) this.getBranchRegional()
		},

		removeHeadquarter({ index, value }) {
			this.companies.splice(index, 1)
		},

		fetchBranches(terms, done) {
			this.$axios
				.get(`/admin_companies/branches/get_branches/?search=${terms}`, this.getHeader)
				.then((response) => {
					let branches = []
					let client = this.clients.find((obj) => obj.id === this.id_user_client_selected)
					if (client.use_create_branch_from_admin_user_page) {
						branches.push({
							value: '-1',
							label: this.$t('other'),
						})
					}
					response.data.forEach((element) => {
						branches.push({
							value: element.id,
							label: `${element.name} / ${element.branch_type_label} - ${
								element.document
							} ${element.register ? ' -' : ''}`,
							address: element.address,
							branch_type: element.branch_type,
							branch_type_label: element.branch_type_label,
							client: element.client,
							company: element.company,
							created_date: element.created_date,
							document: element.document,
							fantasy: element.fantasy,
							managers: element.managers,
							managers_regional: element.managers_regional,
							managers_general: element.managers_general,
							id: element.id,
							name: element.name,
							regional: element.regional,
							status: element.status,
							register: element.register,
							updated_date: element.updated_date,
						})
					})
					done(branches)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		verifyLabelBranch() {
			this.$v.company_branches_label.$touch
			if (this.company_branches.lenght !== this.company_branches_label) {
				this.company_branches_label.pop()
			}
		},

		selectBranch(item, keyboard) {
			if (item.value == -1) this.company_branches = []
			let obj = this.company_branches.find((obj) => obj.value == -1)
			if (obj) {
				this.company_branches = []
				if (this.show_other_branches) this.show_other_branches = false
				this.company_branch_name = ''
			}

			if (!keyboard) {
				let obj = this.company_branches.find((obj) => obj.id === item.id)
				if (!obj) {
					this.company_branches.push(item)
				}

				this.company_branches_label = []
				this.company_branches.forEach((element) => {
					if (element.value == -1) {
						this.company_branches_label.push(`${element.label}`)
						this.show_other_branches = true
					} else {
						this.company_branches_label.push(
							`${element.name} ${element.document ? '- ' : ''} ${
								element.document ? element.document : ''
							} ${element.register ? '- ' : ''} ${
								element.register ? element.register : ''
							}`
						)
					}
				})
			}
		},

		removeBranch({ index, value }) {
			this.company_branches.splice(index, 1)
		},

		openUser() {
			this.$router.push({ name: 'admin_user', params: { id: this.id_user } })
			this.$v.$reset()
			this.email_error = false
			this.document_error = false
			this.register_error = false
			this.id = this.$route.params.id
			if (this.id != 0) this.fetchData()
		},

		getProfileOptions(client) {
			return client.client_profiles
		},

		hasControlPanelAccess(client) {
			if (!client.profile) return true
			let profileObj = client.client_profiles.find((p) => client.profile === p.value)
			let obj = {}

			if (profileObj) {
				obj = this.profiles.find((obj) => obj.label === profileObj.label)
			}

			if (profileObj && obj) {
				if (obj.allow_panel_permissions) {
					return true
				} else {
					client.module_items = []
					return false
				}
			} else return true
		},

		controlPannel(id) {
			let obj = this.user_client_module_items.find((obj) => obj.id === id)
			if (obj) {
				let items = obj.module_items.filter(
					(i) => i.module_slug === 'control-panel' && i.permission
				)
				if (items) return items.sort((a, b) => a.title.localeCompare(b.title))
				else return []
			} else return []
		},

		trainingManager(id) {
			let obj = this.user_client_module_items.find((obj) => obj.id === id)
			if (obj) {
				let items = obj.module_items.filter(
					(i) =>
						(i.module_slug === 'training-manager' ||
							i.module_slug === 'presential_manager') &&
						i.permission
				)
				if (items) return items.sort((a, b) => a.title.localeCompare(b.title))
				else return []
			} else return []
		},

		rewards(id) {
			let obj = this.user_client_module_items.find((obj) => obj.id === id)
			if (obj) {
				let items = obj.module_items.filter(
					(i) => i.module_slug === 'rewards' && i.permission
				)
				if (items) return items.sort((a, b) => a.title.localeCompare(b.title))
				else return []
			} else return []
		},

		getAreas(client) {
			const selected_client = this.user_clients_store.find((obj) => obj.id === client)
			if (selected_client) {
				this.areasClient = selected_client.client_areas
				return selected_client.client_areas
			}
			return []
		},

		getCountries(client) {
			const selected_client = this.user_clients_store.find((obj) => obj.id === client)
			if (selected_client) {
				return selected_client.countries
			}
			return []
		},

		searchManager(terms, done) {
			this.$axios
				.get(`/accounts/get_users/?search=${terms}`, this.getHeader)
				.then((response) => {
					let users = []

					response.data.forEach((element) => {
						users.push({
							value: element.id,
							label: `${element.full_name} - ${element.email}`,
							full_name: element.full_name,
							email: element.email,
							id: element.id,
						})
					})

					done(users)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		selectManager(item, keyboard) {
			if (!keyboard) {
				this.managers_full_data.push(item)
			}
		},

		removeManager({ index, value }) {
			this.managers.splice(index, 1)
			this.managers_full_data.splice(index, 1)
		},

		resetUserPassword() {
			this.reset_user_password = true
			this.saveData()
		},

		loginAsUser() {
			if (this.is_superuser || this.user_login_as_user) {
				let fd = new FormData()
				fd.append('user_id', this.id)

				this.$axios
					.post(`etraining_login/super_user_login_as_user/`, fd, this.getHeader)
					.then((response) => {
						this.$store.dispatch('common/SET_ACCESS_TOKEN', [
							response.data.access_token,
							response.data.refresh_token,
							response.data.user_firebase_token,
						])

						location.reload()
					})
					.catch((e) => {
						this.$q.loading.hide()
						console.log(e)
					})
			}
		},

		getBranchRegional() {
			if (this.companies.length > 0) {
				let data = {
					company: this.companies.map((e) => e.id),
				}

				this.$axios
					.get(
						`admin_companies/branches/get_company_branches_filtered/?${this.$qs.stringify(
							data,
							{ arrayFormat: 'repeat' }
						)}`,
						this.getHeader
					)
					.then((response) => {
						let stores = response.data.map((element) => {
							return {
								value: element.id,
								label: `${element.name} ${
									element.branch_type_label
										? `/ ${this.$t(element.branch_type_label)}`
										: ''
								}  ${element.document ? `- ${element.document}` : ''} ${
									element.register ? ' -' : ''
								}`,
								address: element.address,
								branch_type: element.branch_type,
								branch_type_label: element.branch_type_label,
								client: element.client,
								company: element.company,
								created_date: element.created_date,
								document: element.document,
								fantasy: element.fantasy,
								managers: element.managers,
								managers_regional: element.managers_regional,
								managers_general: element.managers_general,
								id: element.id,
								name: element.name,
								regional: element.regional,
								status: element.status,
								register: element.register,
								updated_date: element.updated_date,
							}
						})

						stores.sort(function(a, b) {
							return a.name > b.name ? 1 : b.name > a.name ? -1 : 0
						})

						this.list_branches = stores
					})
					.catch((e) => {
						this.$q.loading.hide()
						console.log(e)
					})
			} else if (this.company_branches.length > 0) {
				let data = {
					branches: this.company_branches.map((e) => e),
				}

				this.$axios
					.get(
						`admin_companies/branches/get_branches_without_companies/?${this.$qs.stringify(
							data,
							{ arrayFormat: 'repeat' }
						)}`,
						this.getHeader
					)
					.then((response) => {
						let stores = response.data.map((element) => {
							return {
								value: element.id,
								label: `${element.name} ${
									element.branch_type_label
										? `/ ${this.$t(element.branch_type_label)}`
										: ''
								}  ${element.document ? `- ${element.document}` : ''} ${
									element.register ? ' -' : ''
								}`,
								address: element.address,
								branch_type: element.branch_type,
								branch_type_label: element.branch_type_label,
								client: element.client,
								company: element.company,
								created_date: element.created_date,
								document: element.document,
								fantasy: element.fantasy,
								managers: element.managers,
								managers_regional: element.managers_regional
									? element.managers_regional
									: [],
								managers_general: element.managers_general
									? element.managers_general
									: [],
								id: element.id,
								name: element.name,
								regional: element.regional,
								status: element.status,
								register: element.register,
								updated_date: element.updated_date,
							}
						})

						stores.sort(function(a, b) {
							return a.name > b.name ? 1 : b.name > a.name ? -1 : 0
						})

						this.list_branches = stores
					})
					.catch((e) => {
						this.$q.loading.hide()
						console.log(e)
					})
			}
		},

		hasObrigatory(name) {
			switch (name) {
				case 'full_name':
					return this.register_fields.name_mandatory ? '*' : ''
				case 'email':
					return this.register_fields.email_fields_mandatory ? '*' : ''
				case 'document':
					return this.register_fields.document_mandatory ? '*' : ''
				case 'register':
					return this.register_fields.register_mandatory ? '*' : ''
				case 'nick_name':
					return this.register_fields.nick_name_mandatory ? '*' : ''
				case 'birthday':
					return this.register_fields.birthday_mandatory ? '*' : ''
				case 'gender':
					return this.register_fields.gender_mandatory ? '*' : ''
				case 'number_of_children':
					return this.register_fields.number_of_children_mandatory ? '*' : ''
				case 'marital_status':
					return this.register_fields.marital_status_mandatory ? '*' : ''
				case 'address':
					return this.register_fields.address_fields_mandatory ? '*' : ''
				case 'city':
					return this.register_fields.address_fields_mandatory ? '*' : ''
				case 'state':
					return this.register_fields.address_fields_mandatory ? '*' : ''
				case 'company_branches_label':
					return this.register_fields.store_mandatory ? '*' : ''
				case 'company_branches':
					return this.register_fields.store_mandatory ? '*' : ''
				case 'date_hiring':
					return this.register_fields.admission_date_mandatory ? '*' : ''
				case 'managers':
					return this.register_fields.managers_mandatory ? '*' : ''
				case 'managers':
					return this.register_fields.managers_mandatory ? '*' : ''
				case 'phone_mobile':
					return this.register_fields.mobile_phone_fields_mandatory ? '*' : ''
				case 'commercial_phone':
					return this.register_fields.commercial_phone_fields_mandatory ? '*' : ''
				case 'residential_phone':
					return this.register_fields.residential_phone_fields_mandatory ? '*' : ''
				case 'whatsapp_phone':
					return this.register_fields.whatsapp_phone_fields_mandatory ? '*' : ''
				case 'branch_regionals':
					return this.register_fields.regional_mandatory ? '*' : ''
				case 'companies_label':
					return this.register_fields.company_mandatory ? '*' : ''
				case 'register_2':
					return this.register_fields.register_2_mandatory ? '*' : ''
				case 'is_handicapped':
					return this.register_fields.is_handicapped_mandatory ? '*' : ''
				case 'handicap_description':
					return this.register_fields.handicap_description_mandatory ? '*' : ''
				default:
					return ''
			}
		},

		getMainAreas(client) {
			let selected_client = this.user_clients_store.find((obj) => obj.id === client.client)
			if (selected_client) {
				let main_areas = selected_client.client_areas
					.filter((e) => client.areas.includes(e.value))
					.map((e) => ({ ...e, value: e.value, label: `${e.initials} - ${e.label}` }))
				return main_areas
			}
			return []
		},

		enableCustomClientCheck(client) {
			this.current_client = this.getClientName(client)
		},

		canLoginAsUser(clients) {
			let selectedClient = this.user_clients_store.find(
				(obj) => obj.id === this.id_user_client_selected
			)
			if (selectedClient) {
				let userProfile = selectedClient.client_profiles.find((p) => p.is_user_profile)
				if (userProfile) {
					this.user_login_as_user = userProfile.user_login_as_user
				}
			} else this.user_login_as_user = false
		},

		async requestAnonymizeUserData(option = false) {
			this.$q.loading.show()
			try {
				const data = {
					process: option,
					user_id: parseInt(this.$route.params.id),
				}

				await this.$axios.post(
					`/etraining_profile/my_profile/process_account_anonymize/`,
					data,
					this.getHeader
				)
				this.show_anonymize_dialog = false
				this.$q.loading.hide()
				this.$q.notify({
					message:
						option === false
							? this.$t('user_data_delete_rejected')
							: this.$t('user_data_delete_confirmed'),
					type: 'positive',
					icon: 'fal fa-save',
					position: 'top',
				})

				this.$router.push({ name: 'admin_users' })
			} catch (error) {
				this.$q.notify({
					message: this.$t('there_was_an_error_processing_your_request'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
				this.show_anonymize_dialog = false
				this.$q.loading.hide()
			}
		},

		checkUserIsAdmin() {
			if (this.is_superuser) {
				this.is_admin = true
				return
			}

			let obj = this.user_clients_store.find((obj) => obj.id === this.id_user_client_selected)
			if (obj) {
				let userProfile = obj.client_profiles.find((profile) => profile.is_user_profile)
				if (userProfile) this.is_admin = userProfile.is_admin
			} else this.is_admin = false
		},
	},

	created() {
		if (this.countries.length == 1) this.user_country = this.countries[0].value
		this.id = this.$route.params.id
		this.timezone = moment.tz.guess()

		if (this.id != 0) this.fetchData()
		else {
			if (this.user_clients_store.length === 1) {
				this.new_client = this.user_clients_store[0].id
				this.addClient()
			}
		}
		this.enableCustomClientCheck(this.id_user_client_selected)
		this.checkUserIsAdmin()
	},

	validations() {
		return {
			full_name: {
				requiredIf: requiredIf(() => {
					return this.register_fields.name_mandatory
				}),
			},
			email: {
				requiredIf: requiredIf(() => {
					return this.register_fields.email_fields_mandatory
				}),
				email,
				emailUnique() {
					if (!this.email_error || this.email_error_prefix) return true
					else return false
				},
			},
			document: {
				documentUnique() {
					if (!this.document_error && !this.document_invalid) return true
					else return false
				},
				requiredIf: requiredIf(() => {
					return this.register_fields.document && this.register_fields.document_mandatory
				}),
			},
			register: {
				registerUnique() {
					if (!this.register_error) return true
					else return false
				},
				requiredIf: requiredIf(() => {
					return this.register_fields.register_mandatory
				}),
			},

			nick_name: {
				requiredIf: requiredIf(() => {
					return this.register_fields.nick_name_mandatory
				}),
			},
			birthday: {
				requiredIf: requiredIf(() => {
					return this.register_fields.birthday_mandatory
				}),
			},
			gender: {
				requiredIf: requiredIf(() => {
					return this.register_fields.gender_mandatory
				}),
			},
			number_of_children: {
				requiredIf: requiredIf(() => {
					return this.register_fields.number_of_children_mandatory
				}),
			},
			marital_status: {
				requiredIf: requiredIf(() => {
					return this.register_fields.marital_status_mandatory
				}),
			},
			address: {
				requiredIf: requiredIf(() => {
					if (this.register_fields.address_fields_mandatory || this.addressRequired)
						return true
					else return false
				}),
			},
			city: {
				requiredIf: requiredIf(() => {
					if (this.register_fields.address_fields_mandatory || this.addressRequired)
						return true
					else return false
				}),
			},
			state: {
				requiredIf: requiredIf(() => {
					if (this.register_fields.address_fields_mandatory || this.addressRequired)
						return true
					else return false
				}),
			},
			company_branches_label: {
				requiredIf: requiredIf(() => {
					if (!this.register_store.store_as_list) {
						return this.register_fields.store && this.register_fields.store_mandatory
					} else return false
				}),
			},
			company_branches: {
				requiredIf: requiredIf(() => {
					if (this.register_store.store_as_list) {
						return this.register_fields.store && this.register_fields.store_mandatory
					} else return false
				}),
			},
			main_company_branch: {
				requiredIf: requiredIf(() => {
					return this.isChevrolet && this.company_branches.lenght > 0
				}),
			},
			company_branch_name: {
				requiredIf: requiredIf(() => {
					return this.hasCreateStore
				}),
			},
			date_hiring: {
				requiredIf: requiredIf(() => {
					return this.register_fields.admission_date_mandatory
				}),
			},
			managers: {
				requiredIf: requiredIf(() => {
					return this.register_fields.managers_mandatory
				}),
			},
			branch_regionals: {
				requiredIf: requiredIf(() => {
					return this.register_fields.regional_mandatory
				}),
			},
			branch_regionals: {
				requiredIf: requiredIf(() => {
					return this.register_fields.regional_mandatory
				}),
			},
			companies_label: {
				requiredIf: requiredIf(() => {
					return this.register_fields.company_mandatory
				}),
			},
			is_handicapped: {
				requiredIf: requiredIf(() => {
					return this.register_fields.is_handicapped_mandatory
				}),
			},
			handicap_description: {
				requiredIf: requiredIf(() => {
					return (
						this.register_fields.handicap_description_mandatory && this.is_handicapped
					)
				}),
			},
			register_2: {
				register2Unique() {
					if (!this.register_2_error) return true
					else return false
				},
				requiredIf: requiredIf(() => {
					return this.register_fields.register_2_mandatory
				}),
			},
			phone_mobile: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.mobile_phone_fields_mandatory
					}),
				},
			},
			phone_residential: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.residential_phone_fields_mandatory
					}),
				},
			},
			phone_commercial: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.commercial_phone_fields_mandatory
					}),
				},
			},
			phone_whatsapp: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.whatsapp_phone_fields_mandatory
					}),
				},
			},
		}
	},
}
</script>

<style></style>
